/**
 * Per-track sampled-curve cache (W1-15 timeline-host; ARCHITECTURE.md §7.7: "a per-track sampled-curve cache keyed
 * by (trackId, engine.revision, range, px step)").
 *
 * Motion columns and the curve editor draw value curves sampled from the engine (`engine.sampleChannel`, the
 * effective Σ curve exactly as Phira computes it; `engine.sampleEffectiveSpeed` for Scroll speed). Sampling is the
 * expensive part, so samples are cached and reused while the user scrolls, plays or redraws overlays:
 *
 *   - Samples sit on a global beat grid `i · step` (step in beats), so a scrolled range reuses every sample it shares
 *     with the previous one. The grid is cut into chunks of `chunkSamples` samples; the cache key is
 *     (track, revision, step, chunk). That is the "(track, revision, range, px step)" key with the range quantised
 *     to chunks.
 *   - `step` comes from the wanted pixel step (2 px by default) at the current zoom, rounded DOWN to a quarter
 *     octave (2^(k/4) beats), so a slow pinch zoom keeps hitting the cache and samples are never sparser than
 *     asked for.
 *   - A new `revision` of a track (engine.revision after a ChangeSet) drops that track's older chunks at once; other
 *     tracks keep theirs. Old chunks beyond `maxChunks` are evicted least-recently-used.
 *
 * The engine samples evenly spaced SECONDS, while the timeline is linear in BEATS; `beatSamplerFromSeconds` splits
 * a beat range at tempo changes so that each call covers one constant-BPM segment, where beats and seconds are
 * proportional and evenly spaced beats are evenly spaced seconds.
 */

import { upperBoundBy } from '../../core/math/search';
import type { TempoPointRt } from '../../core/time/types';
import type { TimeAxis } from '../types';

/**
 * Fills `out[i]` with the value at beat `beat0 + i · (beat1 - beat0) / (out.length - 1)` (one sample: at beat0).
 * NaN marks "no value" (absent track): the curve is broken there.
 */
export type BeatSampler = (beat0: number, beat1: number, out: Float32Array) => void;

/** Seconds-based sampler with the engine's signature (`sampleChannel(line, prop, layer, t0, t1, out)` curried). */
export type SecondsSampler = (t0: number, t1: number, out: Float32Array) => void;

/** A contiguous run of samples: values[i] is the value at beat `beat0 + i · step`. */
export interface SampledCurve {
  readonly beat0: number;
  readonly step: number;
  readonly values: Float32Array;
}

export interface CurveRequest {
  /** Stable track key, e.g. the TrackId or `${line}:${prop}:${layer|'sum'}`. */
  readonly track: string;
  /** engine.revision (or any number that changes when the curve may have changed). */
  readonly revision: number;
  /** Beat range to cover (inclusive; the result may start a little before and end a little after). */
  readonly beat0: number;
  readonly beat1: number;
  readonly pxPerBeat: number;
  /** Wanted distance between samples in CSS px (default 2, ARCHITECTURE.md §7.7). */
  readonly pxStep?: number;
}

export interface CurveCacheStats {
  readonly hits: number;
  readonly misses: number;
}

export interface CurveCache {
  /** Samples covering [beat0, beat1] for the request, from the cache where possible. */
  sample(req: CurveRequest, sampler: BeatSampler): SampledCurve;
  /** Drops one track's chunks (or everything). */
  invalidate(track?: string): void;
  clear(): void;
  /** Number of cached chunks. */
  readonly size: number;
  readonly stats: CurveCacheStats;
}

export interface CurveCacheOptions {
  /** Samples per chunk (default 256). */
  readonly chunkSamples?: number;
  /** LRU bound on cached chunks (default 4096: about 4 MB of Float32 samples). */
  readonly maxChunks?: number;
}

/** Hard bound on the samples one request may produce (a 16k px tall view at a 1 px step). */
const MAX_SAMPLES = 16384;

/** Quarter-octave exponent k with 2^(k/4) <= step (k is the cache's step key). */
export function stepExponent(step: number): number {
  return Math.floor(Math.log2(step) * 4 + 1e-9);
}

/** The cache's sample step in beats for a pixel step at a zoom (rounded down to a quarter octave). */
export function quantizedStep(pxStep: number, pxPerBeat: number): number {
  const raw = Math.max(1e-9, pxStep) / Math.max(1e-9, pxPerBeat);
  return Math.pow(2, stepExponent(raw) / 4);
}

export function createCurveCache(opts: CurveCacheOptions = {}): CurveCache {
  const chunkSamples = Math.max(8, Math.floor(opts.chunkSamples ?? 256));
  const maxChunks = Math.max(1, Math.floor(opts.maxChunks ?? 4096));
  /** key -> chunk samples; Map order is the LRU order (oldest first). */
  const chunks = new Map<string, Float32Array>();
  /** track -> revision of its cached chunks. */
  const revisions = new Map<string, number>();
  let hits = 0;
  let misses = 0;

  const dropTrack = (track: string): void => {
    const prefix = `${track}\u0000`;
    for (const k of [...chunks.keys()]) if (k.startsWith(prefix)) chunks.delete(k);
    revisions.delete(track);
  };

  const chunk = (
    track: string,
    rev: number,
    k: number,
    step: number,
    c: number,
    sampler: BeatSampler,
  ): Float32Array => {
    const key = `${track}\u0000${String(rev)}\u0000${String(k)}\u0000${String(c)}`;
    const cached = chunks.get(key);
    if (cached !== undefined) {
      hits++;
      chunks.delete(key);
      chunks.set(key, cached);
      return cached;
    }
    misses++;
    const out = new Float32Array(chunkSamples);
    const i0 = c * chunkSamples;
    sampler(i0 * step, (i0 + chunkSamples - 1) * step, out);
    chunks.set(key, out);
    while (chunks.size > maxChunks) {
      const oldest = chunks.keys().next();
      if (oldest.done === true) break;
      chunks.delete(oldest.value);
    }
    return out;
  };

  return {
    sample(req, sampler) {
      const step0 = quantizedStep(req.pxStep ?? 2, req.pxPerBeat);
      const lo = Math.min(req.beat0, req.beat1);
      const hi = Math.max(req.beat0, req.beat1);
      // Never produce more than MAX_SAMPLES samples: coarsen the step for absurd ranges.
      let k = stepExponent(step0);
      let step = Math.pow(2, k / 4);
      while ((hi - lo) / step > MAX_SAMPLES) {
        k += 4;
        step = Math.pow(2, k / 4);
      }
      if (revisions.get(req.track) !== req.revision) {
        dropTrack(req.track);
        revisions.set(req.track, req.revision);
      }
      const iStart = Math.floor(lo / step);
      const iEnd = Math.ceil(hi / step);
      const n = iEnd - iStart + 1;
      const values = new Float32Array(n);
      const c0 = Math.floor(iStart / chunkSamples);
      const c1 = Math.floor(iEnd / chunkSamples);
      for (let c = c0; c <= c1; c++) {
        const data = chunk(req.track, req.revision, k, step, c, sampler);
        const first = c * chunkSamples;
        const from = Math.max(iStart, first);
        const to = Math.min(iEnd, first + chunkSamples - 1);
        values.set(data.subarray(from - first, to - first + 1), from - iStart);
      }
      return { beat0: iStart * step, step, values };
    },
    invalidate(track) {
      if (track === undefined) {
        chunks.clear();
        revisions.clear();
      } else dropTrack(track);
    },
    clear() {
      chunks.clear();
      revisions.clear();
    },
    get size() {
      return chunks.size;
    },
    get stats() {
      return { hits, misses };
    },
  };
}

/**
 * Adapts a seconds sampler to evenly spaced beats across tempo changes: the beat range is split at every tempo
 * point inside it and each constant-BPM part is sampled with one call (seconds are linear in beats there).
 */
export function beatSamplerFromSeconds(
  tempo: { readonly points: readonly TempoPointRt[]; secAtBeatF64(beats: number): number },
  sampleSec: SecondsSampler,
): BeatSampler {
  return (beat0, beat1, out) => {
    const n = out.length;
    if (n === 0) return;
    if (n === 1) {
      const t = tempo.secAtBeatF64(beat0);
      sampleSec(t, t, out);
      return;
    }
    const step = (beat1 - beat0) / (n - 1);
    let i = 0;
    while (i < n) {
      const bi = beat0 + i * step;
      // First tempo point strictly after this sample: samples before it share one constant-BPM segment.
      const p = upperBoundBy(tempo.points, bi, (pt) => pt.beat);
      const next = tempo.points[p]?.beat;
      let j = n - 1;
      if (next !== undefined && step > 0) j = Math.min(n - 1, Math.ceil((next - beat0) / step) - 1);
      if (j < i) j = i;
      sampleSec(tempo.secAtBeatF64(bi), tempo.secAtBeatF64(beat0 + j * step), out.subarray(i, j + 1));
      i = j + 1;
    }
  };
}

/** Min and max of the samples whose beat lies in [beatLo, beatHi] (NaN skipped); null when there are none. */
export function curveValueRange(curve: SampledCurve, beatLo: number, beatHi: number): readonly [number, number] | null {
  const i0 = Math.max(0, Math.floor((beatLo - curve.beat0) / curve.step));
  const i1 = Math.min(curve.values.length - 1, Math.ceil((beatHi - curve.beat0) / curve.step));
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (let i = i0; i <= i1; i++) {
    const v = curve.values[i];
    if (v === undefined || Number.isNaN(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  return lo <= hi ? [lo, hi] : null;
}

/**
 * Strokes a sampled curve on a column: value horizontal (`valueToX`), time vertical through the axis. NaN samples
 * break the line (absent stretches). Uses the context's current stroke style.
 */
export function strokeSampledCurve(
  ctx: CanvasRenderingContext2D,
  curve: SampledCurve,
  axis: Pick<TimeAxis, 'yAtBeat'>,
  valueToX: (v: number) => number,
): void {
  ctx.beginPath();
  let pen = false;
  for (let i = 0; i < curve.values.length; i++) {
    const v = curve.values[i];
    if (v === undefined || !Number.isFinite(v)) {
      pen = false;
      continue;
    }
    const x = valueToX(v);
    const y = axis.yAtBeat(curve.beat0 + i * curve.step);
    if (pen) ctx.lineTo(x, y);
    else {
      ctx.moveTo(x, y);
      pen = true;
    }
  }
  ctx.stroke();
}
